import type { StampPointType } from '@/components/Functional/useStampPointLabel'
import type { StampUnavailableReason, UserStampData } from '@/schemas/v2/user'

/**
 * スタンプ獲得 API のエラーコードを、エラーダイアログ文言の i18n キーに変換する。
 * 対応していないコードはデフォルトの汎用キーを返す。
 */
export const resolveAcquireErrorKey = (code: string | null, stampPointType: StampPointType = 'STAMP'): string => {
  switch (code) {
    case 'STAMP_ALREADY_ACQUIRED':
      if (stampPointType === 'POINT') return 'spot_detail.acquire_error.point_already_acquired'
      if (stampPointType === 'STAMP_POINT') return 'spot_detail.acquire_error.stamp_point_already_acquired'
      return 'spot_detail.acquire_error.stamp_already_acquired'
    case 'STAMP_ALREADY_ACQUIRED_TODAY':
      return 'spot_detail.acquire_error.stamp_already_acquired_today'
    case 'CAMPAIGN_PERIOD_OUT':
      return 'spot_detail.acquire_error.campaign_period_out'
    case 'STAMP_PERIOD_OUT':
      return 'spot_detail.acquire_error.stamp_period_out'
    case 'STAMP_DAY_OUT':
      return 'spot_detail.acquire_error.stamp_day_out'
    case 'STAMP_HOURS_OUT':
      return 'spot_detail.acquire_error.stamp_hours_out'
    case 'PERIOD_OUT':
      return 'spot_detail.acquire_error.period_out'
    case 'STAMP_NOT_AVAILABLE':
      return 'spot_detail.acquire_error.stamp_not_available'
    case 'SHEET_LOCKED':
      return 'spot_detail.acquire_error.sheet_locked'
    case 'STAMP_LOCK_FAILED':
      return 'spot_detail.acquire_error.stamp_lock_failed'
    default:
      return 'spot_detail.acquire_error.message'
  }
}

/**
 * 全スタンプが獲得不可のときに表示する理由の優先順位（先頭ほど優先）。
 *
 * 1つのスポットに複数スタンプが紐づく場合（#2540）、理由が混在し得る
 * （例: スタンプA=獲得済み / スタンプB=期間外）。
 * 「1件でも獲得済みなら獲得済みと表示する」ため、獲得済み系を先に置く。
 * 通算の獲得済みを表す STAMP_ALREADY_ACQUIRED を、リピートスタンプの1日1回制限より上位にする。
 */
const UNAVAILABLE_REASON_PRIORITY: StampUnavailableReason[] = ['STAMP_ALREADY_ACQUIRED', 'STAMP_ALREADY_ACQUIRED_TODAY']

/**
 * 取得済みユーザースタンプ群から、スタンプ獲得ボタンを非活性にすべき理由を決める。
 * 獲得可能（＝ボタン活性）なら null を返す。
 *
 * - 1件でも獲得可能なものがあれば活性のまま（1スポットに複数スタンプが紐づくケースに対応）。
 * - 全件獲得不可のときは UNAVAILABLE_REASON_PRIORITY を優先し、該当がなければ
 *   渡された順（`spot.stamps` の startAt 昇順）で最初に見つかった理由を返す。
 * - `isAvailable=false` なのに理由が null になるのは、バックエンドが追加した未知コードを
 *   スキーマの `catch(null)` が縮退させたケース。理由不明でも押せないことは確かなので
 *   汎用文言に倒し、ボタンを活性のままにしない。
 */
export const resolveStampsUnavailableReason = (
  stamps: Pick<UserStampData, 'isAvailable' | 'unavailableReason'>[],
): StampUnavailableReason | null => {
  if (stamps.length === 0) return null
  if (!stamps.every((s) => !s.isAvailable)) return null

  const reasons = stamps.map((s) => s.unavailableReason).filter((r) => r != null)
  for (const priority of UNAVAILABLE_REASON_PRIORITY) {
    if (reasons.includes(priority)) return priority
  }
  return reasons[0] ?? 'STAMP_NOT_AVAILABLE'
}

/**
 * `UserStampData.unavailableReason` を、スタンプ獲得ボタンのラベル i18n キーに変換する。
 *
 * 曜日・祝日条件（STAMP_DAY_OUT）や1日1回制限（STAMP_ALREADY_ACQUIRED_TODAY）は
 * クライアント側で判定できないため、サーバーが返した理由をそのまま文言に反映する。
 */
export const resolveStampUnavailableLabelKey = (
  reason: StampUnavailableReason,
  stampPointType: StampPointType = 'STAMP',
): string => {
  switch (reason) {
    case 'STAMP_ALREADY_ACQUIRED':
      return stampPointType === 'POINT' ? 'spot_detail.point_acquired' : 'spot_detail.stamp_acquired'
    case 'STAMP_ALREADY_ACQUIRED_TODAY':
      return 'spot_detail.stamp_unavailable.already_acquired_today'
    case 'CAMPAIGN_PERIOD_OUT':
      return 'spot_detail.stamp_unavailable.campaign_period_out'
    case 'STAMP_PERIOD_OUT':
      return stampPointType === 'POINT' ? 'spot_detail.point_out_of_period' : 'spot_detail.stamp_out_of_period'
    case 'STAMP_DAY_OUT':
      return 'spot_detail.stamp_unavailable.day_out'
    case 'STAMP_HOURS_OUT':
      return 'spot_detail.stamp_unavailable.hours_out'
    // シート非表示・購入連動ロックはユーザーに詳細を伝えても行動できないため汎用文言にまとめる
    case 'STAMP_NOT_AVAILABLE':
    case 'SHEET_LOCKED':
      return 'spot_detail.stamp_unavailable.not_available'
  }
}
